import { Zip, ZipPassThrough } from 'fflate'
import type { ExportOptions, ImageJob, MetadataInspection } from '../types'
import { inspectImageMetadata, stripKnownImageMetadata } from '../core/metadata'
import { canvasToBlob, renderRedactedCanvas } from '../core/redaction'
import { decodeImageFile } from './decodeImage'

export interface ExportResult {
  blob: Blob
  inspection: MetadataInspection
  filename: string
}

const extensionForMime: Record<ExportOptions['mime'], string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}

export function safeFilename(original: string, mime: ExportOptions['mime'], appendSafeSuffix: boolean): string {
  const base = original.replace(/\.[^.]+$/, '').replace(/[\\/:*?"<>|]/g, '_').slice(0, 100) || 'image'
  return `${base}${appendSafeSuffix ? '_安全確認済み' : ''}.${extensionForMime[mime]}`
}

export async function exportJob(job: ImageJob, options: ExportOptions): Promise<ExportResult> {
  if (!job.decoded) throw new Error('画像が読み込まれていません')
  const source = await decodeImageFile(job.file)
  try {
    if (source.width !== job.decoded.width || source.height !== job.decoded.height) {
      throw new Error('元画像の寸法が処理開始時から変化しています')
    }
    const canvas = renderRedactedCanvas(source.drawable, source.width, source.height, job.regions, options)
    const encodedBlob = await canvasToBlob(canvas, options.mime, options.quality)
    const blob = await stripKnownImageMetadata(encodedBlob)
    // Release the full-resolution canvas backing store as soon as encoding is complete.
    canvas.width = 1
    canvas.height = 1
    if (blob.type !== options.mime) {
      throw new Error(`このブラウザは選択した保存形式（${options.mime}）で画像を再生成できません`)
    }
    const inspection = await inspectImageMetadata(blob)
    if (!inspection.clean) {
      throw new Error(`再生成後のメタデータ検査に失敗しました: ${inspection.findings.join('、')}`)
    }
    return { blob, inspection, filename: safeFilename(job.name, options.mime, options.appendSafeSuffix) }
  } finally {
    source.release()
  }
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

export async function createBatchZipForJobs(
  jobs: ImageJob[],
  options: ExportOptions,
  maxOutputBytes = 120 * 1024 * 1024,
  onProgress?: (completed: number, total: number) => void,
): Promise<Blob> {
  const chunks: ArrayBuffer[] = []
  const used = new Set<string>()
  let totalBytes = 0
  let resolveZip!: (blob: Blob) => void
  let rejectZip!: (error: Error) => void
  const completed = new Promise<Blob>((resolve, reject) => {
    resolveZip = resolve
    rejectZip = reject
  })
  const zip = new Zip((error, chunk, final) => {
    if (error) {
      rejectZip(error instanceof Error ? error : new Error(String(error)))
      return
    }
    const ownedChunk = new Uint8Array(chunk.byteLength)
    ownedChunk.set(chunk)
    chunks.push(ownedChunk.buffer)
    if (final) resolveZip(new Blob(chunks, { type: 'application/zip' }))
  })

  try {
    for (let index = 0; index < jobs.length; index += 1) {
      const job = jobs[index]
      if (!job) continue
      const result = await exportJob(job, options)
      totalBytes += result.blob.size
      if (totalBytes > maxOutputBytes) {
        throw new Error(`ZIP対象が${Math.round(maxOutputBytes / 1024 / 1024)}MBを超えました。画像を分けて保存してください。`)
      }

      const dot = result.filename.lastIndexOf('.')
      const stem = dot > 0 ? result.filename.slice(0, dot) : result.filename
      const extension = dot > 0 ? result.filename.slice(dot) : ''
      let filename = result.filename
      let sequence = 2
      while (used.has(filename)) {
        filename = `${stem}_${sequence}${extension}`
        sequence += 1
      }
      used.add(filename)

      const entry = new ZipPassThrough(filename)
      entry.mtime = new Date(1980, 0, 1)
      zip.add(entry)
      entry.push(new Uint8Array(await result.blob.arrayBuffer()), true)
      onProgress?.(index + 1, jobs.length)
      await new Promise<void>((resolve) => setTimeout(resolve, 0))
    }
    zip.end()
    return await completed
  } catch (error) {
    zip.terminate()
    throw error
  }
}

export async function shareFile(result: ExportResult): Promise<boolean> {
  const file = new File([result.blob], result.filename, { type: result.blob.type, lastModified: Date.now() })
  if (!navigator.share || !navigator.canShare?.({ files: [file] })) return false
  await navigator.share({ files: [file], title: '安全確認済みの画像' })
  return true
}
